import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { HelpTile } from './HelpTile';
import { HelpVideoDetails } from './HelpVideoDetails';
import { HelpVideoPlayer } from './HelpVideoPlayer';
import { kindLabel } from './help-gallery';
import type { HelpEntry } from './help-types';

/** Seconds between the end of a video and the next one in the queue. */
export const UP_NEXT_SECONDS = 5;

export interface HelpPlayerPanelProps {
  entry: HelpEntry;
  queue: HelpEntry[];
  watched: ReadonlySet<string>;
  onWatched: (id: string) => void;
  onAdvance: () => void;
  onPlayQueued: (entry: HelpEntry) => void;
  /**
   * `stacked` (Help sheet): video, then the text, then "Up next" as tiles.
   * `stage` (Help page): video on the left, its title and text beside it; the queue plays on
   * after the countdown but is not listed.
   */
  layout?: 'stacked' | 'stage';
  /** Stage only: the close button in the text column's header. */
  onClose?: () => void;
}

/**
 * The shared player (Q48): the video and what it is about ("What it does", "How to do it",
 * Key concept), then "Up next". When a video ends it is marked watched, and the next one
 * starts after a short countdown the viewer can cancel.
 */
export function HelpPlayerPanel({
  entry,
  queue,
  watched,
  onWatched,
  onAdvance,
  onPlayQueued,
  layout = 'stacked',
  onClose,
}: HelpPlayerPanelProps) {
  const [countdown, setCountdown] = useState<number | null>(null);
  const next = queue[0] ?? null;

  // A new video resets the countdown.
  useEffect(() => setCountdown(null), [entry.id]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      setCountdown(null);
      onAdvance();
      return;
    }
    const timer = window.setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown, onAdvance]);

  const handleEnded = () => {
    onWatched(entry.id);
    if (next) setCountdown(UP_NEXT_SECONDS);
  };

  const countdownBar = countdown !== null && next && (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm" role="status">
      <span className="min-w-0 flex-1 text-foreground">
        Up next in {countdown}s: <span className="font-semibold">{next.title}</span>
      </span>
      <Button size="sm" variant="ghost" onClick={() => setCountdown(null)}>
        Cancel
      </Button>
      <Button size="sm" onClick={onAdvance}>
        Play now
      </Button>
    </div>
  );

  if (layout === 'stage') {
    // Desktop: the text column is exactly as tall as the video and scrolls inside, so the
    // stage keeps one height whatever is playing. Phones stack the text under the video.
    return (
      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" data-testid="help-player-panel">
        <div className="min-w-0 lg:aspect-video lg:overflow-y-auto lg:[&>*]:h-full">
          <HelpVideoPlayer key={entry.id} entry={entry} autoPlay onEnded={handleEnded} />
        </div>
        <div className="relative min-w-0">
          <div className="flex flex-col gap-3 lg:absolute lg:inset-0">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  {kindLabel(entry) === 'Task' ? 'Video' : kindLabel(entry)}
                </p>
                <h2 className="text-xl font-semibold leading-tight text-foreground">{entry.title}</h2>
              </div>
              {onClose && (
                <Button size="icon" variant="ghost" className="-mr-1 -mt-1 h-8 w-8 shrink-0" onClick={onClose} aria-label="Close player">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <ScrollArea className="lg:min-h-0 lg:flex-1">
              <div className="pr-3">
                <HelpVideoDetails entry={entry} />
              </div>
            </ScrollArea>
            {countdownBar}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="help-player-panel">
      <HelpVideoPlayer key={entry.id} entry={entry} autoPlay onEnded={handleEnded} />
      {countdownBar}
      <h2 className="text-base font-semibold text-foreground">{entry.title}</h2>
      <HelpVideoDetails entry={entry} />
      {queue.length > 0 && (
        <section aria-label="Up next" className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Up next</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {queue.map((queued) => (
              <HelpTile key={queued.id} entry={queued} watched={watched.has(queued.id)} onPlay={onPlayQueued} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
